import { SelectProps } from '@amzn/awsui-components-core';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_MODULE, DEFAULT_TEAM } from '../all-apps-config';

const URL_PARAM_TEAM = 'team';
const URL_PARAM_MODULE = 'module';
const URL_PARAM_SEARCH = 'q';

interface UseAllAppsFilterUrlSyncProps {
  teamOptions: SelectProps.Options;
  moduleOptions: SelectProps.Options;
  team: SelectProps.Option;
  module: SelectProps.Option;
  filteringText: string;
  setTeam: (option: SelectProps.Option) => void;
  setModule: (option: SelectProps.Option) => void;
  setFilteringText: (text: string) => void;
}

export const useAllAppsFilterUrlSync = ({
  teamOptions,
  moduleOptions,
  team,
  module,
  filteringText,
  setTeam,
  setModule,
  setFilteringText
}: UseAllAppsFilterUrlSyncProps): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const didHydrate = useRef(false);

  const findOptionByLabel = useCallback((options: SelectProps.Options, label: string): SelectProps.Option | null => {
    for (const option of options) {
      if ('label' in option && option.label === label) {
        return option as SelectProps.Option;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (didHydrate.current) return;
    if ((teamOptions as SelectProps.Option[]).length <= 1 && (moduleOptions as SelectProps.Option[]).length <= 1) return;

    const params = new URLSearchParams(window.location.search);
    const teamParam = params.get(URL_PARAM_TEAM);
    const moduleParam = params.get(URL_PARAM_MODULE);
    const searchParam = params.get(URL_PARAM_SEARCH);

    if (teamParam) {
      const matchedOption = findOptionByLabel(teamOptions, teamParam);
      if (matchedOption) {
        setTeam(matchedOption);
      }
    }

    if (moduleParam) {
      const matchedOption = findOptionByLabel(moduleOptions, moduleParam);
      if (matchedOption) {
        setModule(matchedOption);
      }
    }

    if (searchParam) {
      setFilteringText(searchParam);
    }

    didHydrate.current = true;
  }, [teamOptions, moduleOptions, findOptionByLabel, setTeam, setModule, setFilteringText]);

  useEffect(() => {
    if (!didHydrate.current) return;

    const next = new URLSearchParams(searchParams);

    const isTeamFiltered = team.value !== DEFAULT_TEAM.value;
    const isModuleFiltered = module.value !== DEFAULT_MODULE.value;
    const hasSearchText = filteringText.trim().length > 0;

    if (isTeamFiltered) {
      next.set(URL_PARAM_TEAM, team.label ?? '');
    } else {
      next.delete(URL_PARAM_TEAM);
    }

    if (isModuleFiltered) {
      next.set(URL_PARAM_MODULE, module.label ?? '');
    } else {
      next.delete(URL_PARAM_MODULE);
    }

    if (hasSearchText) {
      next.set(URL_PARAM_SEARCH, filteringText.trim());
    } else {
      next.delete(URL_PARAM_SEARCH);
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [team, module, filteringText, searchParams, setSearchParams]);
};
